import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from 'services/api';
import useAuthStore from 'stores/authStore';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Input } from 'components/ui/input';
import { Label } from 'components/ui/label';
import { Textarea } from 'components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from 'components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'components/ui/tabs';
import { 
  ArrowLeft, DollarSign, Plus, Receipt, Users, TrendingUp,
  Loader2, Pill, Package, Wrench, Heart, Car, Utensils, Home, MoreHorizontal,
  ArrowRight, Check
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  medications: Pill,
  supplies: Package,
  equipment: Wrench,
  services: Heart,
  transportation: Car,
  food: Utensils,
  housing: Home,
  other: MoreHorizontal
};

const CATEGORY_COLORS = {
  medications: 'bg-purple-100 text-purple-600',
  supplies: 'bg-blue-100 text-blue-600',
  equipment: 'bg-slate-100 text-slate-600',
  services: 'bg-rose-100 text-rose-600',
  transportation: 'bg-green-100 text-green-600',
  food: 'bg-amber-100 text-amber-600',
  housing: 'bg-teal-100 text-teal-600',
  other: 'bg-gray-100 text-gray-600'
};

export default function FamilyExpenses() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [totals, setTotals] = useState({});
  const [balances, setBalances] = useState([]);
  const [netBalances, setNetBalances] = useState({});
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showSettle, setShowSettle] = useState(false);
  const [settleWith, setSettleWith] = useState(null);
  const [settleAmount, setSettleAmount] = useState('');
  const [formData, setFormData] = useState({
    category: 'medications',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0],
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catsRes, expensesRes, balancesRes] = await Promise.all([
        api.get('/expenses/categories'),
        api.get('/expenses?days=90'),
        api.get('/expenses/balances')
      ]);
      setCategories(catsRes.data.categories || []);
      setExpenses(expensesRes.data.expenses || []);
      setTotals(expensesRes.data.totals || {});
      setBalances(balancesRes.data.balances || []);
      setNetBalances(balancesRes.data.net_balances || {});
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddExpense = async (e) => {
    e.preventDefault();
    try {
      await api.post('/expenses', {
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString()
      });
      toast.success('Expense added!');
      setShowAdd(false);
      setFormData({
        category: 'medications', description: '', amount: '',
        date: new Date().toISOString().split('T')[0], notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add expense');
    }
  };

  const handleSettle = async () => {
    if (!settleWith || !settleAmount) return;
    try {
      await api.post('/expenses/settlements', {
        to_user: settleWith.to_user,
        amount: parseFloat(settleAmount)
      });
      toast.success('Payment recorded!');
      setShowSettle(false);
      setSettleWith(null);
      setSettleAmount('');
      fetchData();
    } catch (error) {
      toast.error('Failed to record payment');
    }
  };

  const myBalance = netBalances[user?.user_id]?.amount || 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="expenses-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboard')} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-slate-900 font-['Manrope']">Family Expenses</h1>
              <p className="text-xs sm:text-sm text-slate-500">Track and split care costs</p>
            </div>
          </div>
          <Dialog open={showAdd} onOpenChange={setShowAdd}>
            <DialogTrigger asChild>
              <Button data-testid="add-expense-btn">
                <Plus className="w-4 h-4 mr-2" />
                Add Expense
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Add Expense</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddExpense} className="space-y-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Description *</Label>
                  <Input
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="e.g., Monthly medications from CVS"
                    required
                    data-testid="expense-description"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Amount *</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        type="number"
                        step="0.01"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        className="pl-9"
                        placeholder="0.00"
                        required
                        data-testid="expense-amount"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Notes (optional)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Additional details..."
                    rows={2}
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                  <Button type="submit">Add Expense</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Total (90 days)</p>
              <p className="text-2xl font-bold text-slate-900">
                ${totals.total_amount?.toLocaleString() || '0'}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <p className="text-sm text-slate-500">Your Balance</p>
              <p className={`text-2xl font-bold ${myBalance >= 0 ? 'text-green-600' : 'text-rose-600'}`}>
                {myBalance >= 0 ? '+' : ''}{myBalance.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
              </p>
              <p className="text-xs text-slate-400">{myBalance >= 0 ? 'Others owe you' : 'You owe'}</p>
            </CardContent>
          </Card>

          <Card className="col-span-2">
            <CardContent className="p-4">
              <p className="text-sm text-slate-500 mb-2">By Category</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(totals.by_category || {}).slice(0, 4).map(([cat, amount]) => {
                  const Icon = CATEGORY_ICONS[cat] || MoreHorizontal;
                  return (
                    <Badge key={cat} variant="secondary" className="text-xs">
                      <Icon className="w-3 h-3 mr-1" />
                      {cat}: ${amount.toLocaleString()}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Balances to Settle */}
        {balances.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-teal-500" />
                Balances to Settle
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {balances.map((bal, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                        {bal.from_name?.charAt(0) || '?'}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{bal.from_name}</span>
                        <ArrowRight className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{bal.to_name}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-lg font-semibold text-slate-900">
                        ${bal.amount.toLocaleString()}
                      </span>
                      {bal.from_user === user?.user_id && (
                        <Button size="sm" onClick={() => { setSettleWith(bal); setSettleAmount(bal.amount.toString()); setShowSettle(true); }}>
                          Settle
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settlement Dialog */}
        <Dialog open={showSettle} onOpenChange={setShowSettle}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Record Payment</DialogTitle>
            </DialogHeader>
            {settleWith && (
              <div className="space-y-4">
                <p className="text-slate-600">
                  Record payment from <strong>{settleWith.from_name}</strong> to <strong>{settleWith.to_name}</strong>
                </p>
                <div className="space-y-2">
                  <Label>Amount Paid</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      type="number"
                      step="0.01"
                      value={settleAmount}
                      onChange={(e) => setSettleAmount(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowSettle(false)}>Cancel</Button>
                  <Button onClick={handleSettle}>
                    <Check className="w-4 h-4 mr-2" />
                    Record Payment
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Expense List */}
        <Tabs defaultValue="all" className="space-y-4">
          <TabsList>
            <TabsTrigger value="all">All Expenses</TabsTrigger>
            <TabsTrigger value="mine">My Expenses</TabsTrigger>
          </TabsList>

          <TabsContent value="all" className="space-y-4">
            {expenses.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">No expenses recorded yet</p>
                  <Button className="mt-4" onClick={() => setShowAdd(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add First Expense
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {expenses.map((exp) => {
                  const Icon = CATEGORY_ICONS[exp.category] || MoreHorizontal;
                  const colorClass = CATEGORY_COLORS[exp.category] || 'bg-slate-100 text-slate-600';
                  
                  return (
                    <Card key={exp.expense_id}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-900">{exp.description}</h4>
                              <p className="text-sm text-slate-500">
                                Paid by {exp.paid_by_name} • {new Date(exp.date).toLocaleDateString()}
                              </p>
                              {exp.notes && <p className="text-xs text-slate-400 mt-1">{exp.notes}</p>}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-semibold text-slate-900">
                              ${exp.amount.toLocaleString()}
                            </p>
                            <Badge variant="secondary" className="text-xs capitalize">
                              {exp.category}
                            </Badge>
                          </div>
                        </div>
                        
                        {/* Split Info */}
                        {exp.split_type === 'equal' && exp.split_among?.length > 1 && (
                          <div className="mt-3 pt-3 border-t">
                            <p className="text-xs text-slate-500">
                              Split equally among {exp.split_among.length} people 
                              (${(exp.amount / exp.split_among.length).toFixed(2)} each)
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="mine" className="space-y-4">
            {expenses.filter(e => e.paid_by === user?.user_id).length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Receipt className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p className="text-slate-500">You haven't added any expenses yet</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {expenses.filter(e => e.paid_by === user?.user_id).map((exp) => {
                  const Icon = CATEGORY_ICONS[exp.category] || MoreHorizontal;
                  const colorClass = CATEGORY_COLORS[exp.category] || 'bg-slate-100 text-slate-600';
                  
                  return (
                    <Card key={exp.expense_id}>
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                              <Icon className="w-5 h-5" />
                            </div>
                            <div>
                              <h4 className="font-medium text-slate-900">{exp.description}</h4>
                              <p className="text-sm text-slate-500">
                                {new Date(exp.date).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <p className="text-lg font-semibold text-slate-900">
                            ${exp.amount.toLocaleString()}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
